// Face sign-in inside the PIN prompt (utils/pinPrompt.js): one attempt to recognise whoever is
// at the station camera, with the keypad always there underneath.
//
// Phases: checking (asking GET /face/station) -> looking (camera on, up to 2 pictures) ->
//   matched (the prompt counts down, then resolves the face token as the PIN)
//   fallback (the API's message, or "Couldn't recognise you, type your PIN"; "Try face again")
//   cancelled (the operator started typing; "Try face again")
//   off (no face for this prompt: not a live station, or "Not you?")
// Anything that ends an attempt (typing, Not you, Cancel, a new attempt) bumps a run counter,
// so a late /face/identify answer is ignored.

import { useCallback, useEffect, useRef, useState } from 'react';
import { acquireCamera, captureJpeg, lastCameraError, releaseCamera } from './faceCamera';
import { faceNotMe, identifyFace, readIdentify } from './faceApi';
import { getStationStatus } from './faceStation';

/** Mutable so tests can shorten them. */
export const FACE_TIMING = { retryMs: 800, totalMs: 6000, countdownMs: 1500, attempts: 2 };

export const FACE_FALLBACK_MESSAGE = "Couldn't recognise you, type your PIN";

const RETRY_OUTCOMES = new Set(['no_face', 'no_match']);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function useFacePin(wanted) {
  const [state, setState] = useState(() => ({
    phase: wanted ? 'checking' : 'off',
    match: null,
    message: null,
    stream: null,
  }));
  const runRef = useRef(0);
  const holdingRef = useRef(false);
  const mountedRef = useRef(true);
  const liveRef = useRef(false); // GET /face/station said live: only then may the camera open

  const letGo = useCallback(() => {
    if (holdingRef.current) {
      holdingRef.current = false;
      releaseCamera();
    }
  }, []);

  const run = useCallback(async () => {
    runRef.current += 1;
    const id = runRef.current;
    const alive = () => mountedRef.current && runRef.current === id;
    setState((s) => ({ ...s, phase: 'looking', match: null, message: null }));

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const deadline = Date.now() + FACE_TIMING.totalMs;
    let timer = null;
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve({ timeout: true }), FACE_TIMING.totalMs); });

    const attempt = (async () => {
      let stream = null;
      if (!holdingRef.current) {
        holdingRef.current = true;
        stream = await acquireCamera();
        if (!alive()) return { stale: true };
        if (!stream) return { fallback: lastCameraError() };
        setState((s) => ({ ...s, stream }));
      }
      for (let i = 1; i <= FACE_TIMING.attempts; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const image = await captureJpeg();
        if (!alive()) return { stale: true };
        if (!image) return { fallback: null };
        // eslint-disable-next-line no-await-in-loop
        const answer = readIdentify(await identifyFace(image, controller ? { signal: controller.signal } : {}));
        if (!alive()) return { stale: true };
        if (answer.outcome === 'matched') return { match: answer };
        const more = i < FACE_TIMING.attempts && Date.now() + FACE_TIMING.retryMs < deadline;
        if (!RETRY_OUTCOMES.has(answer.outcome) || !more) return { fallback: answer.message };
        // eslint-disable-next-line no-await-in-loop
        await sleep(FACE_TIMING.retryMs);
        if (!alive()) return { stale: true };
      }
      return { fallback: null };
    })().catch(() => ({ fallback: null }));

    const result = await Promise.race([attempt, timeout]);
    clearTimeout(timer);
    if (!alive() || result.stale) return;
    if (result.timeout) {
      runRef.current += 1; // whatever the attempt still brings back is ignored
      if (controller) controller.abort();
    }
    if (result.match) {
      setState((s) => ({ ...s, phase: 'matched', match: result.match, message: null }));
      return;
    }
    letGo();
    setState({ phase: 'fallback', match: null, message: result.fallback || FACE_FALLBACK_MESSAGE, stream: null });
  }, [letGo]);

  // Start once: only when GET /face/station says this station is live.
  useEffect(() => {
    mountedRef.current = true;
    if (!wanted) return undefined;
    // A form that stays mounted (the Add Sheet popup) turns this on again on every open: start
    // clean, never from the last open's match.
    setState({ phase: 'checking', match: null, message: null, stream: null });
    const startedAt = runRef.current;
    getStationStatus().then((st) => {
      if (!mountedRef.current || runRef.current !== startedAt) return; // typed / closed meanwhile
      if (st && st.live) {
        liveRef.current = true;
        run();
      }
      else setState((s) => ({ ...s, phase: 'off' }));
    });
    return () => {
      mountedRef.current = false;
      runRef.current += 1;
      if (holdingRef.current) {
        holdingRef.current = false;
        releaseCamera();
      }
    };
  }, [wanted, run]);

  /** Typing a digit, or the prompt closing: stop now, ignore any answer still coming. */
  const cancel = useCallback((phase = 'cancelled') => {
    runRef.current += 1;
    letGo();
    setState((s) => {
      if (s.phase === 'off') return s;
      return { phase: liveRef.current ? phase : 'off', match: null, message: null, stream: null };
    });
  }, [letGo]);

  /** "Not you?": revoke the token (fire and forget) and leave face off for this prompt. */
  const notMe = useCallback(() => {
    const token = state.match && state.match.token;
    if (token) faceNotMe(token).catch(() => {});
    cancel('off');
  }, [state.match, cancel]);

  const retry = useCallback(() => {
    if (liveRef.current) run();
  }, [run]);

  return { ...state, cancel, notMe, retry };
}
